import {Component, EventEmitter, Input, Output } from '@angular/core';
import BusinessEntity from '../../../../../services/businessEntity.model';
import FarmSiteModel from '../../../../../services/farmSite.model';
import ItemMasterModel from '../../../../../services/itemMaster.model';
import TransactionTypeModel from '../../../../../services/transactionType.model';
import TransactionsModel, {Items} from '../../../../../services/transactions.model';
import UOMMasterModel from '../../../../../services/uom.model';
import CustomerPondModel from '../../../../../services/customerPond.model';
import Vendors from '../../../../../services/vendor.model';
import VendorDuesModel from '../../../../../services/vendorDues.model';
import PaymentTransactionModel from '../../../../../services/paymentTransaction.model';
import AttachmentsModel from '../../../../../services/attachments.model';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import AttachmentsService from '../../../../../services/attachements.service';
import {PaymentTransactionService} from '../../../../../services/paymentTransaction.service';
import {VendorDuesService} from '../../../../../services/vendorDues.service';
import {VendorsService} from '../../../../../services/vendors.service';
import {CustomerPondService} from '../../../../../services/customerPond.service';
import {ItemMasterService} from '../../../../../services/itemMaster.service';
import {BusinessEntityService} from '../../../../../services/businessEntity.service';
import {FarmSiteService} from '../../../../../services/farmSite.service';
import {TransactionsService} from '../../../../../services/transactions.service';
import {TransactionTypeService} from '../../../../../services/transactionType.service';
import {CommonMethodsService} from '../../../../../services/commonMethods.service';
import * as moment from 'moment';
import * as _ from 'lodash';

declare var $: any;

/**
 * @component MultilineExpenseComponent
 * @selector app-multiline-expense
 *
 * @description
 * The `MultilineExpenseComponent` handles the creation of complex expense transactions that involve multiple line items.
 * It is designed to support inventory management and financial tracking within the farm management system.
 *
 * ## Key Responsibilities:
 * 1. **Transaction Creation**: Allows users to create expense transactions with multiple items (e.g., feed, medicine, equipment).
 * 2. **Inventory Integration**: Updates inventory levels based on purchased items if they are marked as stockable.
 * 3. **Vendor Management**: Associates expenses with specific vendors and handles payment status (Fully Paid, Partially Paid).
 * 4. **File Attachment**: Supports uploading receipts or invoices (images) associated with the transaction.
 * 5. **Validation**: Ensures all required fields (Item, Quantity, UOM, Unit Cost) are valid before submission.
 *
 * ## Features:
 * - **Dynamic Line Items**: Users can add or remove item rows dynamically.
 * - **Auto-Calculation**: Automatically calculates total amounts based on quantity, unit cost, and GST.
 * - **Vendor Filtering**: Autocomplete dropdown for selecting vendors.
 * - **Date Selection**: Date picker for transaction date.
 *
 * ## Data Flow:
 * 1. **Initialization**: Loads vendors, items, and farm sites based on inputs or user context.
 * 2. **Item Selection**: User selects an item; UOMs are populated based on the item's configuration.
 * 3. **Calculation**: `totalAmount()` iterates through items to calculate the grand total.
 * 4. **Submission**: `inventoryTransaction()` constructs the payload, handles file upload to S3, creates the transaction records, and updates payment details.
 *
 * @input businessId - The ID of the business context.
 * @input siteId - The ID of the farm site context.
 * @input businessName - The name of the business.
 * @input locationName - The name of the location.
 * @output gotoMultiLine - Event emitted to navigate or close the component.
 *
 * @service TransactionsService - For creating transactions.
 * @service PaymentTransactionService - For recording payments.
 * @service AttachmentsService - For linking uploaded files.
 * @service ItemMasterService - For fetching product details.
 * @service VendorsService - For fetching vendor details.
 */
@Component({
  selector: 'app-multiline-expense',
  templateUrl: './multiline-expense.component.html',
  styleUrls: ['./multiline-expense.component.scss']
})
export class MultilineExpenseComponent {
  /** @type {Array<BusinessEntity>} List of business entities. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {BusinessEntity} Selected business entity. */
  public businessEntity: BusinessEntity;

  /** @type {Array<FarmSiteModel>} List of farm sites. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<ItemMasterModel>} List of item masters (products). */
  public itemMasters: Array<ItemMasterModel>;

  /** @type {ItemMasterModel} Selected item master. */
  public itemMaster: ItemMasterModel;

  /** @type {Array<TransactionTypeModel>} List of transaction types. */
  public transactionTypes: Array<TransactionTypeModel>;

  /** @type {TransactionTypeModel} Selected transaction type. */
  public transactionType: TransactionTypeModel;

  /** @type {Array<TransactionsModel>} List of transactions. */
  public transactions: Array<TransactionsModel>;

  /** @type {TransactionsModel} Current transaction model. */
  public transaction: TransactionsModel;

  /** @type {Array<UOMMasterModel>} List of UOMs. */
  public uoms: Array<UOMMasterModel>;

  /** @type {UOMMasterModel} Selected UOM. */
  public uom: UOMMasterModel;

  /** @type {Array<CustomerPondModel>} List of customer ponds. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<Vendors>} List of vendors. */
  public vendors: Array<Vendors>;

  /** @type {Vendors} Selected vendor. */
  public vendor: Vendors;

  /** @type {Array<VendorDuesModel>} List of vendor dues. */
  public vendorDues: Array<VendorDuesModel>;

  /** @type {VendorDuesModel} Selected vendor due. */
  public vendorDue: VendorDuesModel;

  /** @type {Array<PaymentTransactionModel>} List of payment transactions. */
  public paymentTransactions: Array<PaymentTransactionModel>;

  /** @type {PaymentTransactionModel} Current payment transaction model. */
  public paymentTransaction: PaymentTransactionModel;

  /** @type {AttachmentsModel} Attachment model for uploads. */
  public mtlAttachmentsModel: AttachmentsModel;

  /** @type {Array<Items>} List of line items in the transaction. */
  public items: Array<Items>;

  /** @type {string} Logged-in user role. */
  public loginPersonType: string;

  /** @type {string} Logged-in user name. */
  public LoginUserName: string;

  /** @type {string} Logged-in user business name. */
  public loginUserBusinessName: string;

  /** @type {string} Logged-in user business category. */
  public loginUserBusinessCategory: string;

  /** @type {boolean} Flag indicating if form is submitted. */
  public issubmitted: boolean;

  /** @type {File | any} Selected image file. */
  public imageFile: File | any;

  /** @type {String | ArrayBuffer} Base64 representation of selected file. */
  public selectedbase64file: String | ArrayBuffer;

  /** @type {boolean} Flag indicating if file selection was cleared. */
  public isCleared: boolean;

  /** @type {boolean} Flag indicating if a file is selected. */
  public isFileSelected: boolean;

  /** @type {Array<any>} List of attachments. */
  public attachments = new Array();

  /** @type {number} Total amount of the transaction. */
  public totalamount: number;

  /** @type {any} Data of the latest created transaction. */
  public latestTransactionData: any;

  /** @type {any} Payment details object. */
  public paymentDetails: any;

  /** @input @type {string} Business ID passed from parent. */
  @Input('businessId') paramsbusinessId: string;

  /** @input @type {string} Farm site ID passed from parent. */
  @Input('siteId') paramsfarmsiteId: string;

  /** @input @type {string} Business name passed from parent. */
  @Input('businessName') businessName: string;

  /** @input @type {string} Location name passed from parent. */
  @Input('locationName') locationName: string;

  /** @output @type {EventEmitter<any>} Event emitted to close/navigate. */
  @Output() gotoMultiLine: EventEmitter<any> = new EventEmitter<any>();

  /** @type {any} Transaction date. */
  public transactionDate: any;

  /** @type {boolean} Flag for form submission status. */
  public submitted = false;

  /** @type {Array<any>} List of inventory items. */
  public inventoryItems = new Array();

  /** @type {string} Name of the uploaded file. */
  public fileName: string;

  /** @type {FormGroup} Reactive form group. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag for vendor dropdown state. */
  public vendorsDropdownOpen: boolean = false;

  /** @type {string} Search string for vendor name. */
  public vendorNameSearch: string;

  /** @type {any[]} Filtered list of vendors. */
  public filteredVendorNames: any[] = [];

  /** @type {boolean} Flag to disable submit button. */
  public submitButtonDisabled: boolean;

  /** @type {Array<any>} List for pond dropdown. */
  public ponddropdownList = [];

  /** @type {Array<any>} Selected pond items. */
  public pondselectedItems = [];

  /** @type {Object} Settings for pond dropdown. */
  public ponddropdownSettings = {};

  /** @type {Array<any>} Detailed pond dropdown list. */
  public pondsDetails_dropdown = new Array();

  /** @type {FormControl} Control for transaction date. */
  public transactionDateControl = new FormControl(new Date());

  /** @type {Date} Maximum allowed date (today). */
  maxDate: Date = new Date();

  /**
   * Constructor for MultilineExpenseComponent.
   * Initializes services and models.
   *
   * @param {ToastrService} toasterService - Service for toast notifications.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {AttachmentsService} mtlattachmentsService - Service for attachment operations.
   * @param {PaymentTransactionService} paymentTransactionservice - Service for payment transactions.
   * @param {VendorDuesService} vendorDuesService - Service for vendor dues.
   * @param {VendorsService} vendorsService - Service for vendor operations.
   * @param {CustomerPondService} customerPondsService - Service for customer pond operations.
   * @param {ItemMasterService} itemMasterService - Service for item master operations.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {TransactionsService} transactionsService - Service for transaction operations.
   * @param {TransactionTypeService} transactionTypeService - Service for transaction type operations.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   */
  constructor(private toasterService: ToastrService, public formBuilder: FormBuilder, public mtlattachmentsService: AttachmentsService, public paymentTransactionservice: PaymentTransactionService, public vendorDuesService: VendorDuesService, public vendorsService: VendorsService, public customerPondsService: CustomerPondService, public itemMasterService: ItemMasterService, public businessEntityService: BusinessEntityService, public farmSiteService: FarmSiteService, public transactionsService: TransactionsService, public transactionTypeService: TransactionTypeService,
              public commonMethodsService: CommonMethodsService) {
    this.paymentTransaction = new PaymentTransactionModel();
    this.transaction = new TransactionsModel();
    this.mtlAttachmentsModel = new AttachmentsModel();
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Initializes form state and flags.
   * - Fetches vendors and items if `paramsbusinessId` is present.
   * - Fetches ponds if `paramsfarmsiteId` is present.
   */
  public async ngOnInit() {
    try {
      this.loginPersonType = localStorage.getItem('loginPersonType');
      /* user login details */
      this.LoginUserName = localStorage.getItem('LoginUserName');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.isFileSelected = false;
      this.imageFile = undefined;
      this.isCleared = false;
      this.selectedbase64file = undefined;
      this.vendorNameSearch = undefined;
      this.submitButtonDisabled = false;
      $('#imageUpload').val('');
      /* user login details end */
      if (this.paramsbusinessId) {
        this.getVendorsByBusinessId();
        this.getAllItemMasterbyFarmer();
      }
      if (this.paramsfarmsiteId) {
        this.getAllpondsByFarmsiteId();
      }
      this.items = new Array();
      this.items.push({
        item_id: undefined,
        uom_id: undefined,
        unit_cost: null,
        quantity: null
      });
      this.transaction.stage = 'Growout';
      this.transaction.transaction_date = (moment(new Date()).format('DD-MMM-YYYY')).toString(); // "2020-05-20"
      this.initializedatePicker();
      this.vendorCtrl.valueChanges.subscribe(value => {
        // If the user types or clears text directly, value will be a string (not a vendor object)
        if (typeof value === 'string') {
          this.paymentTransaction.vendor_id = undefined;
        }
      });
      this.issubmitted = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllpondsByFarmsiteId
   * @async
   * @description Fetches all ponds associated with the current farm site ID.
   * Populates `pondsDetails_dropdown` and `ponddropdownList`.
   */
  public async getAllpondsByFarmsiteId() {
    if (this.paramsfarmsiteId) {
      this.pondsDetails_dropdown = [];
      
      try {
        this.customerPonds = await this.customerPondsService.getPondsByFarmSiteId([this.paramsfarmsiteId]);
        this.pondsDetails_dropdown = [];
        if (this.customerPonds && this.customerPonds.length > 0) {
          for (let i = 0; i < this.customerPonds.length; i++) {
            if ((this.customerPonds[i].PondCultureData && this.customerPonds[i].PondCultureData.length > 0)) {
              let eachpondculture = this.customerPonds[i].PondCultureData[this.customerPonds[i].PondCultureData.length - 1];
              eachpondculture.pond_name = this.customerPonds[i].name;
              eachpondculture.pond_id = this.customerPonds[i].id;
              eachpondculture.isPreparatory = this.customerPonds[i].isPreparatory;
              eachpondculture.pond_identifier = this.customerPonds[i].pond_identifier;
              this.pondsDetails_dropdown.push(eachpondculture);
            } else if (this.customerPonds[i].isPreparatory == true && this.customerPonds[i].PondCultureData.length == 0) {
              let isPreObj = {
                id: null,
                pond_id: this.customerPonds[i].id,
                pond_name: this.customerPonds[i].name,
                culture_identifier: null,
                isPreparatory: this.customerPonds[i].isPreparatory,
                pond_identifier: this.customerPonds[i].pond_identifier
              };
              this.pondsDetails_dropdown.push(isPreObj);
            }
          }
        }
        /*prepare paond array objects*/
        this.ponddropdownList = [];
        for (let k = 0; k < this.pondsDetails_dropdown.length; k++) {
          let newobj = {
            id: this.pondsDetails_dropdown[k].id,
            itemName: this.pondsDetails_dropdown[k].pond_name,
            pondID: this.pondsDetails_dropdown[k].pond_id,
            cultureIdentifier: this.pondsDetails_dropdown[k].culture_identifier,
            pondIdentifier: this.pondsDetails_dropdown[k].pond_identifier,
            isPreparatory: this.pondsDetails_dropdown[k].isPreparatory
          };
          this.ponddropdownList.push(newobj);
        }
        /*prepare paond array objects end*/
      } catch (error) {
        this.commonMethodsService.userFriendlyAlertMessages(error);
      }
    }
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the template.
   * @returns {AbstractControl} Form controls.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @lifecycle ngOnDestroy
   * @description
   * Lifecycle hook that is called when the component is destroyed.
   * Cancels all ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests for injected services.
   */
  public ngOnDestroy(): void {
    this.vendorsService.cancelOnGoingHttpRequests();
    this.itemMasterService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.transactionsService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getAllBusiness
   * @async
   * @description Fetches all business entities (Admin only).
   * Filters for 'Farm' type businesses and loads vendors.
   */
  public async getAllBusiness() {
    try {
      let farmBusiness = await this.businessEntityService.getAllBusiness();
      this.businessEntities = farmBusiness.filter(businessEntity => businessEntity.businessType && businessEntity.businessType.length > 0 && businessEntity.businessType[0].name && businessEntity.businessType[0].name === 'Farm');
      await this.getVendorsByBusinessId();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  vendorCtrl = new FormControl<string | any>('');

  /**
   * @method displayVendor
   * @description Formats the vendor object for display in the autocomplete input.
   * @param {any} vendor - The vendor object.
   * @returns {string} The vendor name.
   */
  public displayVendor(vendor: any | string | null): string {
    return vendor && typeof vendor === 'object' ? vendor.name : '';
  }

  /**
   * @method filterVendors
   * @description Filters the vendor list based on user input.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  public filterVendors(event?: KeyboardEvent): void {
    const value = (event ? (event.target as HTMLInputElement).value : this.vendorCtrl.value) || '';
    const search = typeof value === 'string' ? value.toLowerCase() : value?.name.toLowerCase();
    this.filteredVendorNames = this.vendors
      ?.filter(p => p.name.toLowerCase().includes(search)) ?? [];
  }

  /**
   * @method selectVendorOption
   * @description Handles vendor selection from the autocomplete dropdown.
   * @param {any} vendor - The selected vendor object.
   */
  public selectVendorOption(vendor: any): void {
    $('.custom-select label').css({'top': 0});
    this.vendorsDropdownOpen = false;
    this.vendorNameSearch = vendor.name;
    this.paymentTransaction.vendor_id = vendor.id;
  }

  /**
   * @method getAllItemMasterbyFarmer
   * @async
   * @description Fetches all item masters (products) for the current business.
   * Filters for non-stockable items into `inventoryItems` (logic seems inverted in code comments vs variable name, assuming code is correct).
   */
  public async getAllItemMasterbyFarmer() {
    try {
      this.itemMasters = await this.itemMasterService.getAllProductsByBusinessId(this.paramsbusinessId);
      this.inventoryItems = [];
      for (let k = 0; k < this.itemMasters.length; k++) {
        if (this.itemMasters[k].stockable == false) {
          this.inventoryItems.push(this.itemMasters[k]);
        }
      }
      if (this.inventoryItems.length > 0) {
        for (let u = 0; u < this.inventoryItems.length; u++) {
          let eachItem = this.inventoryItems[u];
          this.inventoryItems[u].item_name = eachItem.item_name + '' + (eachItem.sub_type ? '-' + eachItem.sub_type : '') + '-' + ((eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].to_value) ? '-' + (eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].to_value) : '') + ((eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].ToUOM.name) ? (eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].ToUOM.name) : '');
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getBusinessItemByItemId
   * @description Retrieves and sets the `business_item_id` for a selected item in the items array.
   * @param {number} index - The index of the item in the `items` array.
   * @side-effect Updates `items[index].business_item_id`.
   */
  public getBusinessItemByItemId(index: number) {
    this.issubmitted = false;
    let businessItemIdarr = _.filter(this.itemMasters, (o) => {
      return o.id == this.items[index].item_id;
    });
    if (businessItemIdarr.length > 0) {
      this.items[index].business_item_id = businessItemIdarr[0].BusinessItem[0].id;
    }
  }

  /**
   * @method getItemIdsUomData
   * @description Retrieves UOM data for a selected item and prepares the UOM dropdown.
   * @param {number} index - The index of the item in the `items` array.
   * @side-effect Updates `items[index].itemUom`.
   */
  public getItemIdsUomData(index: number) {
    this.items[index].itemUom = [];
    let itemIdarr = _.filter(this.itemMasters, (o) => {
      return o.id == this.items[index].item_id;
    });
    if (itemIdarr && itemIdarr.length > 0) {
      this.items[index].item_id = itemIdarr[0].id;
      this.prepareUOM(itemIdarr[0], index);
    }
  }

  /**
   * @method prepareUOM
   * @description Prepares the UOM list for a specific item based on its conversion configuration.
   * @param {any} itmsArr - The item master object.
   * @param {number} index - The index of the item in the `items` array.
   */
  public prepareUOM(itmsArr: any, index: number) {
    let uomData = []; // each time i prepare new items in onchane
    if (itmsArr && itmsArr.UOMConversion && itmsArr.UOMConversion.length > 0) {
      let uomdataArr = itmsArr.UOMConversion;
      for (let i = 0; i < uomdataArr.length; i++) {
        let tempfromobj = { 'id': uomdataArr[i].from_units_uom_id, 'name': uomdataArr[i].FromUOM.name };
        let temptoobj = { 'id': uomdataArr[i].to_units_uom_id, 'name': uomdataArr[i].ToUOM.name };
        uomData.push(tempfromobj);
        uomData.push(temptoobj);
        this.items[index].itemUom = uomData;
      }
    } else {
      uomData.push({ 'id': itmsArr.UOMMaster.id, 'name': itmsArr.UOMMaster.name });
      this.items[index].itemUom = uomData;
    }
  }

  /**
   * @method getVendorsByBusinessId
   * @async
   * @description Fetches vendors associated with the current business ID.
   */
  public async getVendorsByBusinessId() {
    this.issubmitted = false;
    try {
      this.vendors = await this.vendorsService.getVendorsByBusinessId(this.paramsbusinessId);
      this.setSingleValueForVendor();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForVendor
   * @description Automatically selects the vendor if only one vendor exists.
   */
  public setSingleValueForVendor() {
    if (this.vendors && this.vendors.length == 1) {
      this.paymentTransaction.vendor_id = Number(this.vendors[0].id); // model-binding key
    }
  }

  /**
   * @method initializedatePicker
   * @description Initializes the jQuery date picker for the transaction date.
   * Sets up onSelect callback to update the form control.
   */
  public initializedatePicker() {
    let strArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let contract_dates = $('#startdate').datepicker({
      maxDate: new Date(),
      onSelect: function (selectedDate) {
        let parsedDate = (selectedDate.split('/')[1] + '-' + strArray[(parseInt(selectedDate.split('/')[0]) - 1)] + '-' + selectedDate.split('/')[2]);
        let option = this.id == 'startdate' ? 'minDate' : 'maxDate',
          instance = $(this).data('datepicker'),
          date = $.datepicker.parseDate(instance.settings.dateFormat || $.datepicker._defaults.dateFormat, selectedDate, instance.settings);
        contract_dates.not(this).datepicker('option', option, date);
        $('#startdate').val(parsedDate);
      }
    });
  }

  /**
   * @method todayDate
   * @description Returns the current date and time formatted as a string.
   * @returns {string} Formatted date string (YYYY-MM-DD HH:mm:ss).
   */
  public todayDate() {
    let year = new Date().getFullYear();
    let month = new Date().getMonth() + 1;
    let dateday = new Date().getDate();
    let hours = new Date().getHours();
    let minutes = new Date().getMinutes();
    let seconds = new Date().getSeconds();
    let dateStr = year + '-' + month + '-' + dateday + ' ' + hours + ':' + minutes + ':' + seconds;
    return dateStr;
  }

  /**
   * @method fileupload
   * @description Handles file selection for image upload.
   * @param {any} event - The file input change event.
   */
  public fileupload(event: any) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 2097152) {
          this.toasterService.clear();
          this.toasterService.error('Image size should not exceed 2MB');
          $('#imageUpload').val('');
        } else {
          this.imageFile = file;
          this.isFileSelected = true;
          this.fileName = file.name;
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            const dataresult = '' + reader.result;
            this.selectedbase64file = dataresult;
          };
          reader.onerror = () => {
          };
        }
      } else {
        this.isFileSelected = false;
      }
    }
  }

  /**
   * @method browseFile
   * @description Triggers the file input click event programmatically.
   */
  public browseFile() {
    const fileInput = document.getElementById('imageUpload') as HTMLInputElement;
    fileInput.click(); // Trigger the file input click event
  }

  /**
   * @method remove
   * @description Removes the selected image file and clears the preview.
   * Prompts for confirmation.
   */
  public remove() {
    const deleteimage = confirm('Are you sure you want to delete these image?');
    if (deleteimage) {
      this.imageFile = undefined;
      this.isFileSelected = false;
      this.isCleared = true;
      this.fileName = undefined;
      this.selectedbase64file = undefined;
      $('#imageUpload').val('');
    }
  }

  /**
   * @method addNewItemButton
   * @description Adds a new line item row to the expense form.
   * Validates the last row before adding a new one.
   */
  public addNewItemButton() {
    if (this.items.length > 0) {
      if (this.items[this.items.length - 1]['item_id'] == undefined) {
        this.toasterService.clear();
        this.toasterService.error('Select item');
        return;
      } else if (this.items[this.items.length - 1]['quantity'] == undefined) {
        this.toasterService.clear();
        this.toasterService.error('Enter quantity');
        return;
      } else if (this.items[this.items.length - 1]['quantity'] <= 0) {
        this.toasterService.clear();
        this.toasterService.error('Enter valid quantity');
        return;
      } else if (this.items[this.items.length - 1]['uom_id'] == undefined) {
        this.toasterService.clear();
        this.toasterService.error('Select UOM');
        return;
      } else if (this.items[this.items.length - 1]['unit_cost'] == undefined) {
        this.toasterService.clear();
        this.toasterService.error('Enter unit cost');
        return;
      } else if (this.items[this.items.length - 1]['unit_cost'] <= 0) {
        this.toasterService.clear();
        this.toasterService.error('Enter unit cost');
        return;
      }
    }
    
    this.items.push({
      item_id: undefined,
      uom_id: undefined,
      unit_cost: null,
      quantity: null,
      gst_percent: null,
      itemUom: []
    });
    this.totalAmount();
  }

  /**
   * @method totalAmount
   * @description Calculates the total amount of the transaction.
   * Iterates through all items and sums up (Quantity * Unit Cost) + GST.
   */
  public totalAmount() {
    this.totalamount = 0;
    for (let i = 0; i < this.items.length; i++) {
      let purchase_amount;
      let eachObj = JSON.parse(JSON.stringify(this.items[i]));
      if (eachObj.quantity && eachObj.unit_cost && eachObj.gst_percent) {
        purchase_amount = ((parseFloat('' + eachObj.quantity) * parseFloat('' + eachObj.unit_cost) * parseFloat('' + eachObj.gst_percent)) / 100) + parseFloat('' + eachObj.quantity) * parseFloat('' + eachObj.unit_cost);
        this.totalamount += purchase_amount; // test key
      } else {
        purchase_amount = parseFloat('' + eachObj.quantity) * parseFloat('' + eachObj.unit_cost);
        this.totalamount += purchase_amount; // test key
      }
    }
  }

  /**
   * @method numberValidation
   * @description Validates input to allow only numbers and decimals.
   * @param {any} event - The keyboard event.
   */
  public numberValidation(event: any) {
    const key = event.key;
    const value = event.target.value;
    // Allow numbers, the dot (decimal point), and the Backspace key
    if (
      !/^\d$/.test(key) && // Check if the entered character is a number
      key !== '.' && // Check if the entered character is a dot (decimal point)
      key !== 'Backspace' // Check if the Backspace key was pressed
    ) {
      event.preventDefault();
    }

    // Allow only one dot (decimal point)
    if (key === '.' && value.includes('.')) {
      event.preventDefault();
    }

    // Disallow the first letter being a dot
    if (key === '.' && value === '') {
      event.preventDefault();
    }
  }

  /**
   * @method removeItemCard
   * @description Removes a line item row from the form.
   * @param {number} i - The index of the item to remove.
   * @returns {boolean} True if removed, false if it was the last item.
   */
  public removeItemCard(i: number) {
    if (this.items.length == 1) {
      this.toasterService.clear();
      this.toasterService.error('Can\'t delete the item card when there is only one card');
      return false;
    } else {
      this.items.splice(i, 1);
      this.toasterService.clear();
      this.toasterService.success('Card deleted successfully', '', {
        timeOut: 2000
      });
      this.totalAmount();
      return true;
    }
  }

  /**
   * @method inventoryTransaction
   * @async
   * @description Submits the expense transaction.
   * Validates inputs, constructs the payload, handles file upload, and calls the transaction service.
   */
  public async inventoryTransaction() {
    this.transaction.transaction_date = moment(this.transactionDateControl.value).format('YYYY-MM-DD');
    this.issubmitted = false;
    if (!this.paramsbusinessId) {
      this.toasterService.clear();
      this.toasterService.error('Please select farm');
      return false;
    }
    if (this.paramsfarmsiteId == null || this.paramsfarmsiteId == 'undefined') {
      this.toasterService.clear();
      this.toasterService.error('Please select farmsite');
      return false;
    }
    if (this.itemMasters.length == 0) {
      this.toasterService.clear();
      this.toasterService.warning('No item available for this business ');
      return false;
    }
    if (!this.transaction.transaction_date) {
      this.toasterService.clear();
      this.toasterService.error('Please select date');
      return false;
    }
    if (!this.transaction.stage) {
      this.toasterService.clear();
      this.toasterService.error('Please select stage');
      return false;
    }
    if (this.items[this.items.length - 1]['item_id'] == undefined) {
      this.toasterService.clear();
      this.toasterService.error('Select item');
      return;
    } else if (this.items[this.items.length - 1]['quantity'] == undefined) {
      this.toasterService.clear();
      this.toasterService.error('Enter quantity');
      return;
    } else if (this.items[this.items.length - 1]['quantity'] <= 0) {
      this.toasterService.clear();
      this.toasterService.error('Enter valid quantity');
      return;
    } else if (this.items[this.items.length - 1]['uom_id'] == undefined) {
      this.toasterService.clear();
      this.toasterService.error('Select UOM');
      return;
    } else if (this.items[this.items.length - 1]['unit_cost'] == undefined) {
      this.toasterService.clear();
      this.toasterService.error('Enter unit cost');
      return;
    } else if (this.items[this.items.length - 1]['unit_cost'] <= 0) {
      this.toasterService.clear();
      this.toasterService.error('Enter unit cost');
      return;
    }
    /*multiple selection of tank or single object*/
    let pondIdsarr = [];
    if (this.pondselectedItems && this.pondselectedItems.length > 0) {
      for (let pondID of this.pondselectedItems) {
        const selectedPond = this.ponddropdownList.find(p => p.pondID === pondID);
        if (selectedPond) {
          let newobj = {
            pond_culture_id: selectedPond.id,
            pond_id: selectedPond.pondID,
            culture_identifier: selectedPond.cultureIdentifier,
            pond_identifier: selectedPond.pondIdentifier,
            isPreparatory: selectedPond.isPreparatory
          };
          pondIdsarr.push(newobj);
        }
      }
    }
    this.transaction.tank_ids = pondIdsarr;
    this.submitButtonDisabled = true;
    const addTransactionReq = [];
    for (let i = 0; i < this.items.length; i++) {
      // const eachObj = JSON.parse(JSON.stringify(this.items[i]));
      this.transaction = JSON.parse(JSON.stringify(this.transaction));
      this.transaction.business_id = this.paramsbusinessId;
      this.transaction.farm_site_id = this.paramsfarmsiteId;
      // this.transaction.transaction_date = this.transactionDate;
      // this.transaction.stage = this.transaction.stage;
      this.transaction.transaction_type_id = 3;
      this.transaction.farm_location = this.locationName;
      this.transaction.business_name = this.businessName;
      this.transaction.updated_by = localStorage.personID;
      this.transaction.created_name = this.LoginUserName;
      this.transaction.updated_name = this.LoginUserName;
      this.transaction.created_by = localStorage.personID;
      this.transaction.created_date = this.todayDate();
      this.transaction.updated_date = this.todayDate();
      this.transaction.item_id = this.items[i].item_id;
      this.transaction.business_item_id = Number(this.items[i].business_item_id);
      this.transaction.unit_cost = Number(this.items[i].unit_cost);
      this.transaction.gst_percent = Number(this.items[i].gst_percent || 0);
      this.transaction.gst_value = Number((this.items[i].quantity * this.items[i].unit_cost * this.items[i].gst_percent) / 100);
      this.transaction.value = Number(this.items[i].quantity && this.items[i].unit_cost && this.items[i].gst_percent ? (((this.items[i].quantity * this.items[i].unit_cost * this.items[i].gst_percent) / 100) + (this.items[i].quantity * this.items[i].unit_cost)) : this.items[i].quantity * this.items[i].unit_cost);
      if (this.items[i].itemUom[0].uomadded == true && this.items[i].uom_id == 3) {
        this.transaction.quantity = Number(this.items[i].quantity) / 1000;
        this.transaction.uom_id = 1;
      } else {
        this.transaction.quantity = Number(this.items[i].quantity);
        this.transaction.uom_id = Number(this.items[i].uom_id);
      }
      this.paymentTransaction.vendor_id = this.paymentTransaction.vendor_id;
      if (this.loginPersonType != 'EMPLOYEE') {
        delete this.transaction.business_id;
        delete this.transaction.business_name;
        delete this.transaction.created_name;
        delete this.transaction.updated_name;
        delete this.transaction.created_by;
        delete this.transaction.updated_by;
      }
      addTransactionReq.push(this.transaction);
    }
    this.paymentDetails = [];
    if (this.paymentTransaction.invoice_number) {
      if (this.paymentTransaction.invoice_number.length > 15) {
        this.toasterService.clear();
        this.toasterService.error('Invoice number should be 15 characters');
        this.submitButtonDisabled = false;
        return;
      }
    }
    if (this.paymentTransaction.invoice_number && (this.vendors.length == 0)) {
      this.toasterService.clear();
      this.toasterService.warning('Vendors are not associated for this business');
      return false;
    } else if (this.paymentTransaction.invoice_number && (Number(this.paymentTransaction.paid_amount) && !Number.isFinite(this.paymentTransaction.vendor_id))) {
      this.toasterService.clear();
      this.toasterService.error('Please select vendor');
      this.submitButtonDisabled = false;
      return;
    }
    if ((Number(this.paymentTransaction.paid_amount)) && (this.vendors.length == 0)) {
      this.toasterService.clear();
      this.toasterService.warning('Vendors are not associated for this business');
      this.submitButtonDisabled = false;
      return false;
    } else if ((Number(this.paymentTransaction.paid_amount)) && (Number(this.paymentTransaction.paid_amount) && !Number.isFinite(this.paymentTransaction.vendor_id))) {
      this.toasterService.clear();
      this.toasterService.error('Please select vendor');
      this.submitButtonDisabled = false;
      return false;
    }
    if (this.transaction.notes && this.transaction.notes.length > 200) {
      this.toasterService.clear();
      this.toasterService.error('Maximum 200 characters allowed for description');
      this.submitButtonDisabled = false;
      return false;
    }
    if ((this.paymentTransaction.vendor_id != undefined || this.paymentTransaction.paid_amount)) {
      this.getPaymentTransaction(addTransactionReq);
    } else {
      try {
        this.transactions = await this.transactionsService.createTransaction(addTransactionReq);
        if (this.transactions) {
          this.latestTransactionData = this.transactions;
          let image = this.imageFile;
          let transactionIds =  new Array();
          for (let i=0;i < this.latestTransactionData.transactionResult[0].transactions.length; i++) {
            transactionIds.push(this.latestTransactionData.transactionResult[0].transactions[i].id);
          }
          if (this.isFileSelected) {
            const upload = this.transactionsService.uploadFileToS3Bucket(this.imageFile, this.latestTransactionData.business_name, 'Material_Tx', `MM-${this.imageFile.name}`);
            upload.subscribe((res) => {
              if (res) {
                for(let i=0; i < transactionIds.length;i++) {
                  this.mtlAttachmentsModel.file_name = image.name;
                  this.mtlAttachmentsModel.file_type = image.type.split('/')[1];
                  this.mtlAttachmentsModel.attachment_path = Object.values(res)[0];
                  this.mtlAttachmentsModel.transaction_id = Number(transactionIds[i]);
                  this.mtlattachmentsService.create(this.mtlAttachmentsModel);
                }
              } else {
                this.toasterService.clear();
                this.toasterService.error('File upload failed--')
                this.submitButtonDisabled = false;
              }
            })
          }
          this.toasterService.clear();
          this.toasterService.success('Transaction created successfully', '', {
            timeOut: 2000
          });
          $('#imageUpload').val('');
          this.vendorNameSearch = undefined;
          this.imageFile = undefined;
          this.isFileSelected = false;
          this.isCleared = false;
          this.selectedbase64file = undefined;
          this.submitted = false;
          this.fileName = undefined;
          this.issubmitted = true;
          this.totalamount = 0;
          this.submitButtonDisabled = false;
          this.items = new Array();
          this.vendorCtrl.reset();                 // for vendor input
          this.filteredVendorNames = [];          // clear filtered vendor list
          this.pondselectedItems = [];
          this.paymentTransaction.vendor_id = undefined;
          this.items.push({
            item_id: undefined,
            uom_id: undefined,
            unit_cost: null,
            quantity: null,
            gst_percent: null
          });
          this.pondselectedItems = [];
          this.transaction.transaction_date = moment(new Date()).format('DD-MMM-YYYY'); // "2020-05-20"
          this.transactionDateControl.setValue(new Date());
        }
      } catch (error) {
        this.commonMethodsService.userFriendlyAlertMessages(error);
        this.submitButtonDisabled = false;
      }
    }
  }

  /**
   * @method getPaymentTransaction
   * @async
   * @description Handles transaction creation when payment details are involved.
   * @param {any} transaction - The transaction payload.
   */
  public async  getPaymentTransaction(transaction: any) {
    const quantity = [];
    const uomIds = [];
    const valuesArr = [];
    if (transaction && transaction.length > 0) {
      for (let i = 0; i < transaction.length; i++) {
        quantity.push(transaction[i].quantity);
        uomIds.push(Number(transaction[i].uom_id));
        valuesArr.push(Number(transaction[i].value));
      }
    }
    this.paymentTransaction.value = valuesArr;
    this.paymentTransaction.quantity = quantity;
    this.paymentTransaction.uom_id = uomIds;
    this.paymentTransaction.purchase_amount = Number(this.totalamount);
    this.paymentTransaction.business_id = this.paramsbusinessId;
    this.paymentTransaction.farm_site_id = this.paramsfarmsiteId;
    this.paymentTransaction.value = valuesArr;
    if (this.paymentTransaction.invoice_number) {
      this.paymentTransaction.invoice_number = this.paymentTransaction.invoice_number;
    }
    if (this.paymentTransaction.paid_amount == null) {
      this.paymentTransaction.paid_amount = 0;
    } else {
      this.paymentTransaction.paid_amount = Number(this.paymentTransaction.paid_amount);
    }
    if (this.paymentTransaction.paid_amount == this.totalamount) { //
      this.paymentTransaction.payment_status = 'Fully';
    } else if (this.paymentTransaction.paid_amount < (this.totalamount)) {
      this.paymentTransaction.payment_status = 'Partially';
    } else if (this.paymentTransaction.paid_amount > (this.totalamount)) {
      this.toasterService.clear();
      this.toasterService.warning('Amount should be lessthan value or same');
      this.submitButtonDisabled = false;
      return false;
    }
    this.paymentTransaction.date = moment(this.transactionDateControl.value).format('YYYY-MM-DD')
    this.paymentTransaction.transaction_type_id = 3;
    this.paymentTransaction.vendor_id = Number(this.paymentTransaction.vendor_id);
    try {
      this.transactions = await this.transactionsService.createTransaction(transaction);
      if (this.transactions) {
        this.latestTransactionData = this.transactions;
        let image = this.imageFile;
        let transactionIds =  new Array();
        let itemIds = new Array();
        for (let i=0;i < this.latestTransactionData.transactionResult.length; i++) {
          transactionIds.push(this.latestTransactionData.transactionResult[i].transactions[0].id);
          itemIds.push(this.latestTransactionData.transactionResult[i].transactions[0].item_id)
        }
        if (this.isFileSelected) {
          const upload = this.transactionsService.uploadFileToS3Bucket(this.imageFile, this.latestTransactionData.business_name, 'Material_Tx', `MM-${this.imageFile.name}`);
          upload.subscribe((res) => {
            if (res) {
              for(let i=0; i < transactionIds.length;i++) {
                this.mtlAttachmentsModel.file_name = image.name;
                this.mtlAttachmentsModel.file_type = image.type.split('/')[1];
                this.mtlAttachmentsModel.attachment_path = Object.values(res)[0];
                this.mtlAttachmentsModel.transaction_id = Number(transactionIds[i]);
                this.mtlattachmentsService.create(this.mtlAttachmentsModel);
              }
            } else {
              this.toasterService.clear();
              this.toasterService.error('File upload failed--');
              this.submitButtonDisabled = false;
            }
          })
        }
        this.toasterService.success('Transaction created successfully', '', {
          timeOut: 2000
        });
        /*if (this.transactions && this.latestTransactionData.transactionResult.length > 0) {
          for (let i = 0; i < this.latestTransactionData.transactionResult.length; i++) {
            let eachObj = this.latestTransactionData.transactionResult[i];
            materialTransactionIds.push(eachObj.transactions[0].id);
          }
        }*/
        this.imageFile = undefined;
        this.isFileSelected = false;
        this.isCleared = false;
        this.selectedbase64file = undefined;
        this.fileName = undefined;
        this.submitted = false;
        this.issubmitted = true;
        this.totalamount = 0;
        this.submitButtonDisabled = false;
        this.items = new Array();
        this.items.push({
          item_id: undefined,
          uom_id: undefined,
          unit_cost: null,
          quantity: null,
          gst_percent: null
        });
        this.transaction.transaction_date = moment(new Date()).format('DD-MMM-YYYY'); // "2020-05-20"
        this.transactionDateControl.setValue(new Date());
        this.paymentTransaction.material_transaction_id = transactionIds;
        this.paymentTransaction.item_id = itemIds;
        this.paymentTransaction = await this.paymentTransactionservice.addPaymentTransaction(this.paymentTransaction);
        this.paymentDetails = this.paymentTransaction;
        this.paymentTransaction.vendor_id = undefined;
        this.paymentTransaction.invoice_number = '';
        this.paymentTransaction.paid_amount = undefined;
        this.vendorNameSearch = undefined;
        this.vendorCtrl.reset();                 // for vendor input
        this.filteredVendorNames = [];          // clear filtered vendor list
        this.pondselectedItems = [];
        this.paymentTransaction.vendor_id = undefined;
        this.submitButtonDisabled = false;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.submitButtonDisabled = false;
    }
  }

  /**
   * @method clearTransactionData
   * @description Resets the transaction form and data to initial state.
   * @param {any} $event - The click event.
   */
  public clearTransactionData($event: any) {
    if (this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Business Admin' && this.loginUserBusinessCategory != 'Farm') {
      this.vendors = [];
    }
    this.vendorNameSearch = undefined;
    this.submitButtonDisabled = false;
    this.transaction = new TransactionsModel();
    this.paymentTransaction = new PaymentTransactionModel();
    this.mtlAttachmentsModel = new AttachmentsModel();
    this.ngOnInit();
    this.totalamount = 0;
    $event.stopPropagation();
    $event.preventDefault();
    this.vendorCtrl.reset();                 // for vendor input
    this.filteredVendorNames = [];          // clear filtered vendor list
    this.pondselectedItems = [];
    this.paymentTransaction.vendor_id = undefined;
    this.vendor.name = '';
  }
}
